/**
 * ReferenceEstimateDialog - opens the evidence behind a grounded estimate figure's [N] citation.
 *
 * Preferred path: the grounding ref carries the matched ("golden") line item(s) it contributed,
 * frozen at estimate time - so the dialog HIGHLIGHTS exactly those at the top ("Used for this
 * estimate"), then offers a toggle to reveal the REST of that deal's lines (de-emphasized) for
 * context. The full sheet is fetched on open to power that toggle; the matched lines render
 * immediately and never wait on it. Fallback (estimates generated before golden-evidence capture
 * carry no embedded items): fetch the source corpus doc and show the whole sheet flat, titled by
 * the resolved provenance label. Thin chrome around the shared Dialog primitive.
 */

import type { EstimateGroundingRef } from '@protoss/shared';
import { Cross1Icon, Dialog, IconSize, SkeletonBlock } from '@protoss/ui';
import * as React from 'react';
import { salesApi, type ReferenceEstimateDetail } from '../services/salesApi';

interface ReferenceEstimateDialogProps {
  /** The grounding ref to open (matched golden items + provenance label), or null when closed. */
  groundingRef: EstimateGroundingRef | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** A single effort band on a row (golden bands have no `key`; sheet bands have both). */
type EvidenceBand = { label?: string; key?: string; hours: number };

/** One displayable evidence row - a golden snapshot OR a full-sheet fallback item. */
interface EvidenceRow {
  functionalArea: string;
  description?: string;
  bands: EvidenceBand[];
  note?: string;
}

/**
 * Content signature (functional area + band hours) used to match a sheet line to the embedded
 * "golden" matched line(s), so the same line isn't shown twice (once highlighted, once in "the
 * rest"). functionalArea + hours come from the same source row, so equality is exact and survives
 * duplicate functional areas.
 */
function sig(functionalArea: string, bands: Array<{ hours: number }>): string {
  return `${functionalArea.trim().toLowerCase()}|${bands.map((b) => b.hours).join(',')}`;
}

/**
 * Clean a raw band label/key ("Estimate LOW (Hours)", "p50", ...) into a short column header. The
 * low/med/high vocabulary collapses to "Low/Med/High Hours" (the common case); anything else keeps a
 * tidied version of its own label so percentile or custom bands still read sensibly.
 */
function bandHeader(raw: string): string {
  const s = raw.toLowerCase();
  if (/\blow\b/.test(s) || /\bp?10\b/.test(s)) return 'Low Hours';
  if (/\bhigh\b/.test(s) || /\bp?90\b/.test(s)) return 'High Hours';
  if (/\b(med|mid|expected|likely|p?50)\b/.test(s)) return 'Med Hours';
  const cleaned = raw
    .replace(/\(.*?\)/g, '')
    .replace(/\b(estimate|hours?)\b/gi, '')
    .trim();
  return cleaned ? `${cleaned} Hours` : 'Hours';
}

/**
 * Derive the hours columns shared by every table in the dialog from the bands present in the data.
 * Bands are positional (ordered low->high in the source), and golden bands carry no `key`, so columns
 * align by INDEX, not key. A single band across all rows collapses to one neutral "Hours" column;
 * otherwise one column per band position, headed from the best label seen at that position. This is
 * how "low/med/high if the workspace estimates that way, else just hours" falls out of the data: a
 * firm that estimates in bands produces multi-band reference sheets, a single-point firm produces one.
 */
function deriveBandColumns(rows: EvidenceRow[]): string[] {
  const maxBands = rows.reduce((m, r) => Math.max(m, r.bands.length), 0);
  if (maxBands <= 1) return ['Hours'];
  return Array.from({ length: maxBands }, (_, i) => {
    const labelled = rows.map((r) => r.bands[i]).find((b) => b && (b.label || b.key));
    return labelled ? bandHeader(labelled.label ?? labelled.key ?? '') : `Band ${i + 1} Hours`;
  });
}

/** A thead whose hours columns match `deriveBandColumns` (functional area first, taking the space). */
function EvidenceHead({ bandColumns }: { bandColumns: string[] }) {
  return (
    <thead>
      <tr>
        <th>Functional area</th>
        {bandColumns.map((h, i) => (
          <th key={i} className="reference-estimate-dialog__hours-col">
            {h}
          </th>
        ))}
      </tr>
    </thead>
  );
}

/** Render just the <tr> rows for an evidence table (shared by the matched / rest / flat groups). */
function EvidenceRows({
  rows,
  bandColumns,
  rowClass,
}: {
  rows: EvidenceRow[];
  bandColumns: string[];
  rowClass?: string;
}) {
  return (
    <>
      {rows.map((row, i) => (
        <tr key={i} className={rowClass}>
          <td>
            <span className="reference-estimate-dialog__area">{row.functionalArea}</span>
            {row.description ? (
              <span className="reference-estimate-dialog__desc">{row.description}</span>
            ) : null}
            {row.note ? <span className="reference-estimate-dialog__meta">{row.note}</span> : null}
          </td>
          {bandColumns.map((_, col) => {
            const band = row.bands[col];
            return (
              <td key={col} className="reference-estimate-dialog__hours-col">
                {band ? `${band.hours}h` : '-'}
              </td>
            );
          })}
        </tr>
      ))}
    </>
  );
}

export function ReferenceEstimateDialog({
  groundingRef,
  open,
  onOpenChange,
}: ReferenceEstimateDialogProps) {
  const embeddedItems = groundingRef?.items;
  const hasEmbedded = !!embeddedItems && embeddedItems.length > 0;
  const documentUuid = groundingRef?.documentUuid ?? null;

  const [detail, setDetail] = React.useState<ReferenceEstimateDetail | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [showRest, setShowRest] = React.useState(false);

  // Reset transient state whenever the opened ref changes (or the dialog closes): collapse "the
  // rest" and drop any cached/failed sheet so a previous citation never bleeds into the next one.
  React.useEffect(() => {
    setShowRest(false);
    setDetail(null);
    setError(null);
  }, [documentUuid, open]);

  // The full source sheet is fetched LAZILY, never on open - the matched lines already carry their
  // own detail (frozen at estimate time), so the EMBEDDED view renders complete on first paint with
  // no async, no skeleton, no flicker. The fetch fires only when the user expands "Rest of this
  // deal" (a deliberate action) or, for the LEGACY path (no embedded items), when there's nothing
  // else to show. The result is cached for the life of the open dialog so re-expanding is instant.
  const needSheet = open && !!documentUuid && (!hasEmbedded || showRest);
  React.useEffect(() => {
    if (!needSheet || detail) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    salesApi
      .getReferenceEstimate(documentUuid!)
      .then((d) => {
        if (!cancelled) setDetail(d);
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load reference');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [needSheet, detail, documentUuid]);

  const title =
    groundingRef?.sourceLabel ??
    detail?.label ??
    detail?.title ??
    groundingRef?.title ??
    'Reference estimate';

  const matchedSigs = new Set(
    hasEmbedded ? embeddedItems.map((it) => sig(it.functionalArea, it.bands)) : []
  );

  // The whole sheet, mapped to rows + their signatures (only present once the lazy fetch resolves).
  const sheetEntries: Array<{ row: EvidenceRow; signature: string }> = detail
    ? detail.set.items.map((it) => {
        const note = [it.assumptions, it.risks, it.notes]
          .filter((s) => s && s.trim() !== '')
          .join(' · ');
        const row: EvidenceRow = {
          functionalArea: it.functionalArea,
          ...(it.description ? { description: it.description } : {}),
          bands: it.bands,
          ...(note ? { note } : {}),
        };
        return { row, signature: sig(it.functionalArea, it.bands) };
      })
    : [];

  // The matched ("golden") line(s) that grounded this figure - shown highlighted, on top, rendered
  // entirely from the frozen embedded evidence (description + note + bands), so they paint instantly
  // with full detail and never change after the sheet loads (no pop).
  const matchedRows: EvidenceRow[] = hasEmbedded
    ? embeddedItems.map((it) => ({
        functionalArea: it.functionalArea,
        bands: it.bands,
        ...(it.description ? { description: it.description } : {}),
        ...(it.note ? { note: it.note } : {}),
      }))
    : [];

  // Embedded path: everything in the deal that ISN'T a matched line. Legacy path: the whole sheet.
  const restRows: EvidenceRow[] = hasEmbedded
    ? sheetEntries.filter((e) => !matchedSigs.has(e.signature)).map((e) => e.row)
    : [];

  // One hours-column layout shared by every table in the dialog, so the matched / rest / flat groups
  // line up. The matched (embedded) bands fix the columns up front; the rest comes from the same
  // sheet so it never adds new columns.
  const bandColumns = deriveBandColumns(
    hasEmbedded ? [...matchedRows, ...restRows] : sheetEntries.map((e) => e.row)
  );

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="dialog-overlay" />
        <Dialog.Content
          className="dialog-content reference-estimate-dialog"
          aria-describedby={undefined}
        >
          <Dialog.Title className="dialog-title reference-estimate-dialog__title">
            {title}
          </Dialog.Title>
          <Dialog.Close className="dialog-close" aria-label="Close">
            <Cross1Icon width={IconSize.sm} height={IconSize.sm} aria-hidden="true" />
          </Dialog.Close>
          <div className="reference-estimate-dialog__body">
            {hasEmbedded ? (
              <>
                <p className="reference-estimate-dialog__caption">
                  {matchedRows.length === 1 ? 'Line' : 'Lines'} used for this estimate.
                </p>
                <table className="reference-estimate-dialog__table">
                  <EvidenceHead bandColumns={bandColumns} />
                  <tbody>
                    <EvidenceRows
                      rows={matchedRows}
                      bandColumns={bandColumns}
                      rowClass="reference-estimate-dialog__row--matched"
                    />
                  </tbody>
                </table>
                {documentUuid ? (
                  <>
                    <button
                      type="button"
                      className="reference-estimate-dialog__toggle"
                      onClick={() => setShowRest((s) => !s)}
                      aria-expanded={showRest}
                    >
                      {showRest ? 'Hide other lines' : 'Show other lines from this deal'}
                    </button>
                    {showRest ? (
                      loading ? (
                        <SkeletonBlock />
                      ) : error ? (
                        <p className="reference-estimate-dialog__error">{error}</p>
                      ) : restRows.length > 0 ? (
                        <>
                          <p className="reference-estimate-dialog__group-label">Rest of this deal</p>
                          <table className="reference-estimate-dialog__table">
                            <tbody>
                              <EvidenceRows
                                rows={restRows}
                                bandColumns={bandColumns}
                                rowClass="reference-estimate-dialog__row--muted"
                              />
                            </tbody>
                          </table>
                        </>
                      ) : detail ? (
                        <p className="reference-estimate-dialog__group-label">
                          No other lines in this deal.
                        </p>
                      ) : null
                    ) : null}
                  </>
                ) : null}
              </>
            ) : loading ? (
              <SkeletonBlock />
            ) : error ? (
              <p className="reference-estimate-dialog__error">{error}</p>
            ) : detail ? (
              <table className="reference-estimate-dialog__table">
                <EvidenceHead bandColumns={bandColumns} />
                <tbody>
                  <EvidenceRows rows={sheetEntries.map((e) => e.row)} bandColumns={bandColumns} />
                </tbody>
              </table>
            ) : null}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
